import React, { useEffect, useState } from "react";
import { SectionCard, Stat, SourceLink, money } from "./common";
import { TField, TSelect } from "./forms";
import { GoldButton, OutlineButton, EmptyState, ui } from "@/components/Primitives";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { Plus, Receipt, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function FinancialsTab({ property, currency, options }) {
  const [fin, setFin] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "expense", amount: "", category: "Maintenance", description: "", date: new Date().toISOString().slice(0, 10) });
  const [saving, setSaving] = useState(false);
  const load = () => api.get(`/properties/${property.id}/financials`).then((r) => setFin(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [property.id]);

  const add = async () => {
    if (!Number(form.amount)) return toast.error("Enter an amount");
    setSaving(true);
    try {
      await api.post(`/properties/${property.id}/transactions`, { ...form, amount: Number(form.amount) });
      toast.success("Transaction added"); setOpen(false);
      setForm({ ...form, amount: "", description: "" }); load();
    } catch { toast.error("Could not add"); } finally { setSaving(false); }
  };

  if (!fin) return <div className="text-[#8A7F70] text-sm">Loading…</div>;
  const c = fin.categories || {};
  const lines = [
    ["Interest", c["Interest"]], ["Council Rates", c["Council Rates"]],
    ["Insurance", c["Insurance"]], ["Maintenance", c["Maintenance"]],
    ["Body Corporate", c["Body Corporate"]], ["Property Management Fees", c["Property Management Fees"]],
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat label="Income" value={money(fin.income, currency)} accent="#5a9a6a" testid="fin-income" />
        <Stat label="Expenses" value={money(fin.expenses, currency)} accent="#b06b6b" testid="fin-expenses" />
        <Stat label="Net Cash Flow" value={money(fin.net_cash_flow, currency)} accent={fin.net_cash_flow >= 0 ? "#5a9a6a" : "#b06b6b"} testid="fin-net" />
        <Stat label="Tax Deductible" value={money(fin.tax_deductible, currency)} accent="#8a6d2f" testid="fin-deductible" />
      </div>

      <SectionCard title="Expense breakdown">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {lines.map(([label, val]) => <Stat key={label} label={label} value={money(val, currency)} />)}
        </div>
      </SectionCard>

      <SectionCard title="Recent Transactions" action={
        <GoldButton data-testid="fin-add-txn" onClick={() => setOpen(true)}><Plus size={15} /> Add transaction</GoldButton>
      }>
        {fin.recent_transactions.length === 0 ? (
          <EmptyState icon={Receipt} title="No transactions yet" hint="Add income & expenses for this property. Each one keeps its source document." />
        ) : (
          <div className="divide-y divide-[#F0E9DD]">
            {fin.recent_transactions.map((t) => (
              <div key={t.id} data-testid={`fin-txn-${t.id}`} className="py-3 flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-sm text-[#2C2A28] truncate">{t.description || t.category}</div>
                  <div className="text-xs text-[#A99F90] flex items-center gap-2">
                    {t.date} · {t.category}
                    {t.source?.document_id && <SourceLink docId={t.source.document_id} testid={`fin-source-${t.id}`} />}
                  </div>
                </div>
                <div className="font-mono-data text-sm shrink-0" style={{ color: t.type === "income" ? "#5a9a6a" : "#b06b6b" }}>
                  {t.type === "income" ? "+" : "-"}{money(t.amount, currency)}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={ui.dialog}>
          <DialogHeader><DialogTitle className="font-display text-2xl font-light">Add property transaction</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-1">
            <TSelect label="Type" value={form.type} options={["expense", "income"]} onChange={(v) => setForm({ ...form, type: v })} testid="fin-form-type" />
            <TField label="Amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} testid="fin-form-amount" />
            <TSelect label="Category" value={form.category} options={options.categories || []} onChange={(v) => setForm({ ...form, category: v })} testid="fin-form-category" />
            <TField label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} testid="fin-form-date" />
            <div className="col-span-2">
              <TField label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="fin-form-desc" />
            </div>
          </div>
          <DialogFooter>
            <OutlineButton onClick={() => setOpen(false)}>Cancel</OutlineButton>
            <GoldButton data-testid="fin-form-save" onClick={add} disabled={saving}>{saving ? <Loader2 size={15} className="animate-spin" /> : null} Add</GoldButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
